import { Button, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Ellipsis, FolderClosed, SquarePen } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFolderEditMutation } from "web_myplann/Api/Document/useFolderEditMutation";
import type { DocumentTagResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";
import { clampLinesSx } from "web_myplann/Shared/Utils/sxUtils";

type Props = {
	item: DocumentTagResponseDTO;
};

export const FolderItem = ({ item }: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const { mutate: renameFolder } = useFolderEditMutation();

	const [renameValue, setRenameValue] = useState(item.title);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isDrawerConcealed, setIsDrawerConcealed] = useState(false);
	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);

	const handleOpenFolder = () => {
		navigate(Routing.Portfolio.FolderDetail.path(item.title));
	};

	const handleRename = () => {
		renameFolder({
			requestContract: {
				titleToEdit: item.title,
				title: renameValue,
			},
		});
		setIsRenameDialogOpen(false);
		setIsDrawerOpen(false);
	};

	return (
		<Stack key={item.title}>
			<Stack
				direction="row"
				alignItems="center"
				spacing={theme.spacingTokens.xl}
				py={theme.spacingTokens.xl}
				px={theme.spacingTokens.lg}
				onClick={handleOpenFolder}
			>
				<Stack>
					<FolderClosed color={theme.palette.text.primary} size={24} />
				</Stack>
				<Typography
					fontSize={15}
					fontWeight={500}
					sx={{
						...clampLinesSx(2),
						flex: 1,
					}}
				>
					{item.title}
				</Typography>
				<IconButton
					edge="end"
					onClick={(e) => {
						e.stopPropagation();
						setIsDrawerOpen(true);
						setIsDrawerConcealed(false);
					}}
				>
					<Ellipsis size={16} color={theme.palette.deprecated.grey[500]} />
				</IconButton>
			</Stack>

			<MyPlannDrawer
				open={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				title={t(Resources.Portfolio.List.FolderActionsDrawer.title)}
				concealed={isDrawerConcealed}
			>
				<Stack
					px={theme.spacingTokens.xl}
					pb={theme.spacingTokens.xl}
					gap={theme.spacingTokens.xl}
					alignItems="center"
					direction="row"
					onClick={() => {
						setIsRenameDialogOpen(true);
						setIsDrawerConcealed(true);
					}}
				>
					<SquarePen color={theme.palette.primary.main} />
					<Typography>
						{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.rename)}
					</Typography>
				</Stack>
			</MyPlannDrawer>
			<MyPlannDrawer
				open={isRenameDialogOpen}
				onClose={() => {
					setIsRenameDialogOpen(false);
					setIsDrawerOpen(false);
					setRenameValue(item.title);
				}}
				title={t(Resources.Portfolio.List.FolderActionsDrawer.Actions.rename)}
			>
				<Stack
					px={theme.spacingTokens.xl}
					pb={theme.spacingTokens.xl}
					gap={theme.spacingTokens.md}
				>
					<MyPlannInput
						label={t(
							Resources.Portfolio.List.DocumentActionsDrawer.Rename
								.folderRename,
						)}
						value={renameValue}
						onChange={(e) => setRenameValue(e.target.value)}
					/>
					<Button onClick={handleRename}>{t(Resources.Common.save)}</Button>
				</Stack>
			</MyPlannDrawer>
		</Stack>
	);
};
